import registry from "../locales/languages.json";

type Language = { name: string; intl: string; countries: string[] };

const LANGUAGES: Record<string, Language> = registry.languages;

/** The same cookie the marketing site sets when somebody picks a language there. */
export const LOCALE_COOKIE = "mxl_locale";

export const DEFAULT_LOCALE: string = registry.default;

export const LOCALES: readonly string[] = Object.keys(LANGUAGES);

export const LANGUAGE_NAMES: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(LANGUAGES).map(([code, language]) => [code, language.name]),
);

const ONE_YEAR = 60 * 60 * 24 * 365;

export function isLocale(value: unknown): value is string {
  return typeof value === "string" && LOCALES.includes(value);
}

/** The tag `Intl` formats dates and numbers with, which can be more specific than the language. */
export function intlLocaleFor(locale: string | undefined): string | undefined {
  return locale ? (LANGUAGES[locale]?.intl ?? locale) : undefined;
}

export function localeForCountry(country: string | null | undefined): string | undefined {
  if (!country) return undefined;
  return Object.entries(LANGUAGES).find(([, language]) => language.countries.includes(country))?.[0];
}

export function readLocaleCookie(cookie: string): string | undefined {
  for (const part of cookie.split(";")) {
    const [key, ...value] = part.trim().split("=");
    if (key === LOCALE_COOKIE) {
      const locale = value.join("=");
      return isLocale(locale) ? locale : undefined;
    }
  }
  return undefined;
}

/**
 * `?lang=` on a link into the dashboard: the marketing site adds it to every
 * link from a page in another language, so a visitor arrives in the language
 * they were reading without a request to find out.
 */
export function localeFromQuery(search: string): string | undefined {
  const requested = new URLSearchParams(search).get("lang");
  return isLocale(requested) ? requested : undefined;
}

/**
 * Shared with the marketing site only when the dashboard is a subdomain of it,
 * which is the case in every deployed environment; locally both are ports on
 * `localhost` and a host-only cookie is already shared.
 */
export function localeCookieDomain(hostname: string, siteUrl: string): string | undefined {
  try {
    const site = new URL(siteUrl).hostname;
    return hostname !== site && hostname.endsWith(`.${site}`) ? site : undefined;
  } catch {
    return undefined;
  }
}

export function localeCookie(locale: string | null, hostname: string, siteUrl: string, secure: boolean): string {
  const domain = localeCookieDomain(hostname, siteUrl);
  return [
    `${LOCALE_COOKIE}=${locale ?? ""}`,
    "Path=/",
    `Max-Age=${locale ? ONE_YEAR : 0}`,
    "SameSite=Lax",
    ...(domain ? [`Domain=${domain}`] : []),
    ...(secure ? ["Secure"] : []),
  ].join("; ");
}

/** What the page can decide before asking anybody: a link's `?lang=`, then a remembered choice. */
export function immediateLocale(search: string, cookie: string): string | undefined {
  return localeFromQuery(search) ?? readLocaleCookie(cookie);
}
